import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
// Models
import { MultipleApiResponse, PilotLogbookBackend, PilotLogTableItem } from '@mocca-technologies/model';

import { PilotLogbookTemplate } from '../../logbook-templates/pilot-logbook-template/pilot-logbook-template.component';
import { selectedValues } from '../../logbooks-menu/logbooks-menu.component';
// Components
import { OperationNatureSelectorCellComponent } from '../../../render/opNatureSelector';
// Services
import { LogbookService } from 'src/app/services/logbooks.service';
// Utils
import { environment } from 'src/environments/environment';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { CoreService } from 'src/app/services/core.service';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-pilot-table',
  templateUrl: './pilot-table.component.html',
  styleUrls: ['./pilot-table.component.scss'],
  encapsulation: ViewEncapsulation.None,
})

export class PilotTableComponent implements OnInit {

  /**
   * Input variables
   */

  @Input() rows: PilotLogTableItem[];
  @Input() pilotLogbook: PilotLogbookBackend;
  @Input() selectedValues: selectedValues;

  /**
   * Variables
   */

  gridContext = {
    componentParent: this,
    alertController: this.alertController
  };

  columnsPilotLog = [];

  private destroy$ = new Subject<void>();

  components = {
    operationNatureSelector: OperationNatureSelectorCellComponent,
  }

  logType = 'pilot';

  constructor(private modalCtrl: ModalController,
    private alertController: AlertController,
    private logbooksService: LogbookService,
    private coreService: CoreService,
    private translateService: TranslateService
  ) {}

  // #region --> Life Cycle Methods

  ngOnInit() {
    this.initColumns();
        
    this.translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.initColumns();
      });
  }

  // #endregion --> Life Cycle Methods

  // #region --> Datatable Methods

  /**
   * This method is used to save the operation nature of a flight
   * @param rowData
   * @param newActivity
   */
  saveOperationNature(rowData: PilotLogTableItem, newActivity: {value: string, label: string}) {
    !environment.production && console.log('Saving operation nature:', newActivity, 'for row:', rowData);
    !environment.production && console.log('FO UUID is: ', rowData?.flightOperationUuid);

    // Update the data
    rowData.activity = newActivity.label;

    this.logbooksService.updatePilotLogActivity(newActivity.value, rowData.flightOperationUuid).subscribe({
      next: (res: HttpResponse<MultipleApiResponse>) => {
        this.coreService.showServerResponse(res?.status, res?.body?.responseMessage)
      },
      error: (error: HttpErrorResponse) => {
        this.coreService.showServerResponse(error?.status, error?.error);
      }
    });
  }

  // #endregion --> Datatable Methods

  /**
   * Check if current screen is mobile view
   */
  isMobileView(): boolean {
    return window.innerWidth < 600;
  }

  isDesktopView(): boolean {
    return window.innerWidth >= 1024;
  }


  // #region --> Page Methods

  /**
   * Method to open the document visualizer with the pilot logbook
   */

  async downloadPilotLogbook() {

    const modal = await this.modalCtrl.create({
      component: PilotLogbookTemplate,
      cssClass: 'templateModal',
      componentProps: {
        rows: this.rows,
        pilotLogbook: this.pilotLogbook,
        selectedPilot: this.selectedValues.selectedPilot,
      },
    });
    modal.present();

  }

  initColumns() {
    this.columnsPilotLog = [
      { field: 'flightOperationUuid', headerName: 'Flight Operation UUID', autoHeaderHeight: true, wrapText: true, autoHeight: true, resizable: true, minWidth: 180, hide: true },
      { field: 'date', headerName: this.translateService.instant('PAGES.LOGBOOKS.PILOT_LOGBOOK.DATE'), autoHeaderHeight: true, wrapText: true, autoHeight: true, resizable: true, minWidth: 120 },
      { field: 'location', headerName: this.translateService.instant('PAGES.LOGBOOKS.PILOT_LOGBOOK.LOCATION'), autoHeaderHeight: true, wrapText: true, autoHeight: true, resizable: true, minWidth: 260 },
      { field: 'takeoffTime', headerName: this.translateService.instant('PAGES.LOGBOOKS.PILOT_LOGBOOK.TAKEOFF_TIME'), autoHeaderHeight: true, wrapText: true, autoHeight: true, resizable: true, minWidth: 150 },
      { field: 'landingTime', headerName: this.translateService.instant('PAGES.LOGBOOKS.PILOT_LOGBOOK.LANDING_TIME'), autoHeaderHeight: true, wrapText: true, autoHeight: true, resizable: true, minWidth: 150 },
      { field: 'drone', headerName: this.translateService.instant('PAGES.LOGBOOKS.PILOT_LOGBOOK.DRONE'), autoHeaderHeight: true, wrapText: true, autoHeight: true, resizable: true, minWidth: 170 },
      { field: 'flightDuration', headerName: this.translateService.instant('PAGES.LOGBOOKS.PILOT_LOGBOOK.FLIGHT_DURATION'), autoHeaderHeight: true, wrapText: true, autoHeight: true, resizable: true, minWidth: 130 },
      { field: 'numFlights', headerName: this.translateService.instant('PAGES.LOGBOOKS.PILOT_LOGBOOK.NUMBER_OF_FLIGHTS'), autoHeaderHeight: true, wrapText: true, autoHeight: true, resizable: true, minWidth: 120 },
      { field: 'activity', headerName: this.translateService.instant('PAGES.LOGBOOKS.PILOT_LOGBOOK.ACTIVITY'), cellRenderer: 'operationNatureSelector', autoHeaderHeight: true, wrapText: true, autoHeight: true, resizable: true, minWidth: 220 },
      { field: 'role', headerName: this.translateService.instant('PAGES.LOGBOOKS.PILOT_LOGBOOK.ROLE'), autoHeaderHeight: true, wrapText: true, autoHeight: true, resizable: true, minWidth: 130 },
      { field: 'notes', headerName: 'Notes', autoHeaderHeight: true, wrapText: true, autoHeight: true, resizable: true, minWidth: 180, hide: true },
    ];
  }

  // #endregion --> Page Methods

}
